import { useEffect, useState} from 'react'
import Dropdown from 'react-dropdown'
import IconSelectArrow from '@/svgs/select-arrow.svg'
import classes from './ProductGiftSubForm.module.scss'
import LoadingState from '@/components/LoadingState'
import { getMetafield } from '@/utils/getMetafield'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import { getProductByHandle } from '@/utils/getProducts'
import Options from '@/components/Options/Options'
import { getSelectedVariant } from '@/utils/getSelectedVariant'

function buildSelectors(allProducts) {
  return allProducts.reduce((carry, product) => {
    const productHandle = product.content.handle
    const selectorDetailsMetafield = getMetafield({
      product,
      key: 'selector_details',
    })
    const productObj = {
      product,
      boxOption: {
        value: productHandle,
        label: (
          <div
            className={
              classes['subscription-selector__product-selector-details']
            }
          >
            <span>{product.content.title}</span>{' '}
            {selectorDetailsMetafield && (
              <span> • {selectorDetailsMetafield.value}</span>
            )}{' '}
          </div>
        ),
      },
      shipmentsOptions: [], // prepaid options
    }
    let subscriptionGroup = product.metafields.find(
      (metafield) => metafield.key === 'gifted_subscription_group',
    )
    if (subscriptionGroup) {
      subscriptionGroup = JSON.parse(subscriptionGroup.value)
      productObj.shipmentsOptions = subscriptionGroup.prepaid_options.map(
        (option) => option.duration,
      )
    }
    carry[productHandle] = productObj
    return carry
  }, {})
}

const ProductGiftSubForm = ({
  selectedVariant,
  setSelectedVariant,
  allProducts,
  product,
  onSelectProduct,
}) => {
  const { addItemsToOrder, updateGiftOptionMetadata, allSubsInCartAreGiftable } =
    useHeadlessCheckoutContext()
  const selectors = buildSelectors(allProducts)
  const [shipmentSelected, setShipmentSelected] = useState(
    selectors[selectedVariant.productHandle].shipmentsOptions[0],
  )

  const [selectedOptions, setSelectedOptions] = useState(
    product.variants[0].content?.selectedOptions,
  )
  const [isLoading, setIsLoading] = useState(false)
  const options = Object.keys(selectors).map((key) => {
    return selectors[key].boxOption
  })

  const handleAddItem = async (event) => {
    event.preventDefault()
    setIsLoading(true)

    const newItems = [
      {
        variant: selectedVariant,
        properties: {
          interval_text: 'monthly',
          membership_type: 'prepaid',
          is_gift_sub_order: 'true',
        },
        product,
      },
    ]

    const prepaid = await getProductByHandle('prepaid-subscription')
    const prepaidVariant = prepaid.variants.find(
      (v) =>
        v.sku.replace('PREPAID-', '') === selectedVariant.sku.split('-')[0],
    )

    newItems.push({
      variant: {
        ...prepaidVariant,
        selectedOptions: selectedVariant.content.selectedOptions,
      },
      product: prepaid,
      properties: {
        interval_text: 'monthly',
        membership_type: 'prepaid',
      },
      quantity: parseInt(shipmentSelected) - 1,
      is_gift_sub_order: 'true',
    })

    await addItemsToOrder({ items: newItems, redirectToCart: true })

    await updateGiftOptionMetadata({
      is_gift_order: 'true',
    })
    
    setIsLoading(false)
  }

  useEffect(() => {
    setShipmentSelected(
      selectors[selectedVariant.productHandle].shipmentsOptions[0],
    )
  }, [product])

  const handleOptionChange = (event, option) => {
    const newOption = { name: option.name, value: event.target.value }
    const optionIndex = selectedOptions.findIndex(
      (selectedOption) => selectedOption.name === newOption.name,
    )

    const newSelectedOptions = [...selectedOptions]
    if (optionIndex > -1) {
      newSelectedOptions.splice(optionIndex, 1, newOption)
      setSelectedOptions([...newSelectedOptions])
    } else {
      setSelectedOptions([...newSelectedOptions, newOption])
    }
    const variant = getSelectedVariant({
      variants: product.variants,
      options: newSelectedOptions,
    })
    setSelectedVariant(variant ? { ...variant } : null)
  }

  return (
    <form
      onSubmit={(e) => handleAddItem(e)}
      className={classes['product-gift-subscription-form']}
    >
      <ul className={classes['product-subscription-selector']}>
        {/* Box Option */}
        <li
          className={`${classes['product-subscription-selector__item']} ${classes['flex-column']}`}
        >
          <h2 className="heading__pdp-label">
            Box Option
          </h2>
          <Dropdown
            className={`dropdown-selector`}
            options={options.map((option) => option)}
            onChange={(e) => onSelectProduct(e)}
            value={options.find(
              (option) => option.value === selectedVariant.productHandle,
            )}
            arrowClosed={
              <IconSelectArrow className="dropdown-selector__arrow-closed" />
            }
            arrowOpen={
              <IconSelectArrow className="dropdown-selector__arrow-open" />
            }
          />
          {options &&
            options.length > 0 &&
            product?.content?.options.map(
              (option, i) =>
                option.values.length > 1 && (
                  <Options key={option.name} style={classes['variant-options']}>
                    {option.values.map((value) => {
                      const isSelected =
                        selectedVariant.content.selectedOptions[0].value ===
                        value
                      return (
                        <Options.Item
                          name={`product-options-${i}`}
                          key={value}
                          label={option.name === 'amount' ? `$${value}` : value}
                          onChange={($event) =>
                            handleOptionChange($event, option)
                          }
                          value={value}
                          checked={isSelected}
                          style={
                            isSelected
                              ? classes['option-selected']
                              : classes['option']
                          }
                        />
                      )
                    })}
                  </Options>
                ),
            )}
        </li>
        {/* Shipments */}
        <li
          className={`${classes['product-subscription-selector__item']} ${classes['product-subscription-selector__shipment']}`}
        >
          <h2 className="heading__pdp-label">
            Shipments
          </h2>
          <ul>
            {selectors[selectedVariant.productHandle].shipmentsOptions.map(
              (option, index) => {
                return (
                  <li
                    className={classes['subscription-selector__shipment-item']}
                    key={index}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setShipmentSelected(option)
                      }}
                      className={`${classes['subscription-selector__shipment-item-btn']} btn ${shipmentSelected === option ? 'sitkablue' : 'pureWhite'}`}
                    >
                      <span>{option}</span>
                      {option >= 12 && <p>5% Discount</p>}
                    </button>
                  </li>
                )
              },
            )}
          </ul>
        </li>
      </ul>
      <button
        type="submit"
        disabled={isLoading || !allSubsInCartAreGiftable}
        className="btn salmon"
      >
        {isLoading ? <LoadingState /> : 'Add to Cart'}
      </button>
      {allSubsInCartAreGiftable ? (
        <p className={classes['tooltiptext']}>
          {' '}
          <br />
          By adding a gift to your cart, it will turn everything in your cart
          into a gift order.
        </p>
      ) : (
        <p className={classes['tooltiptext']}>
          {' '}
          <br />
          You have a non-gift subscription in your cart. Please remove it before
          adding a gift.
        </p>
      )}
    </form>
  )
}

export default ProductGiftSubForm
